import { useState, useMemo } from "react";
import { Search, Sparkles, BookOpen, MessageCircle, Compass } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useHelpFaq } from "@/hooks/useHelpFaq";
import { HelpChat } from "./HelpChat";
import { tourSteps } from "@/lib/helpContent";

interface HelpCenterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: "kom-igang" | "faq" | "ai";
  isAuthenticated: boolean;
  onStartTour: () => void;
}

export function HelpCenterDialog({
  open,
  onOpenChange,
  defaultTab = "kom-igang",
  isAuthenticated,
  onStartTour,
}: HelpCenterDialogProps) {
  const [tab, setTab] = useState(defaultTab);
  const [search, setSearch] = useState("");
  const { items, loading } = useHelpFaq();

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter(
      (i) =>
        i.question.toLowerCase().includes(s) || i.answer.toLowerCase().includes(s),
    );
  }, [items, search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Hjälpcenter
          </DialogTitle>
          <DialogDescription>
            Få guidning, svar på vanliga frågor eller fråga vår AI-assistent direkt.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="w-full">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="kom-igang" className="gap-1.5">
              <Compass className="h-4 w-4" />
              <span className="hidden sm:inline">Kom igång</span>
            </TabsTrigger>
            <TabsTrigger value="faq" className="gap-1.5">
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Vanliga frågor</span>
              <span className="sm:hidden">FAQ</span>
            </TabsTrigger>
            <TabsTrigger value="ai" className="gap-1.5">
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Fråga AI</span>
              <span className="sm:hidden">AI</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="kom-igang" className="space-y-4 mt-4">
            <div className="rounded-lg border p-4 bg-accent/30">
              <h3 className="font-semibold mb-2">Guidad rundtur</h3>
              <p className="text-sm text-muted-foreground mb-3">
                {isAuthenticated
                  ? "Vi visar dig de viktigaste funktionerna på 5 steg."
                  : "Logga in för att starta den guidade rundturen."}
              </p>
              <Button
                disabled={!isAuthenticated}
                onClick={() => {
                  onOpenChange(false);
                  setTimeout(onStartTour, 200);
                }}
              >
                Starta rundturen
              </Button>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Snabbstart</h3>
              <ol className="space-y-2 text-sm">
                {tourSteps.map((s) => (
                  <li key={s.id} className="flex gap-3">
                    <span className="font-semibold text-primary">{s.title.split(".")[0]}.</span>
                    <span className="text-muted-foreground">{s.body}</span>
                  </li>
                ))}
              </ol>
            </div>
          </TabsContent>

          <TabsContent value="faq" className="space-y-3 mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Sök i vanliga frågor…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <div className="max-h-[50vh] overflow-y-auto pr-1">
              {loading ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Laddar…</p>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Inga frågor matchar din sökning. Prova att fråga AI istället.
                </p>
              ) : (
                <Accordion type="single" collapsible className="w-full">
                  {filtered.map((f) => (
                    <AccordionItem key={f.id} value={f.id}>
                      <AccordionTrigger className="text-left">{f.question}</AccordionTrigger>
                      <AccordionContent className="whitespace-pre-wrap text-sm text-muted-foreground">
                        {f.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </TabsContent>

          <TabsContent value="ai" className="mt-4">
            <HelpChat isAuthenticated={isAuthenticated} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
